import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const QUICK_LINKS = [
  {
    to: '/',
    icon: '🏠',
    label: 'Home Base',
    desc: 'Return to the main hub',
    color: 'from-primary-500/20 to-primary-600/5 border-primary-500/20 hover:border-primary-500/50',
    glow: 'group-hover:text-primary-400',
  },
  {
    to: '/games',
    icon: '🎮',
    label: 'Games',
    desc: 'Browse our full catalog',
    color: 'from-secondary-400/20 to-secondary-600/5 border-secondary-400/20 hover:border-secondary-400/50',
    glow: 'group-hover:text-secondary-400',
  },
  {
    to: '/about',
    icon: '⚔️',
    label: 'About Us',
    desc: 'Meet the Riven Byte team',
    color: 'from-yellow-500/20 to-yellow-600/5 border-yellow-500/20 hover:border-yellow-500/50',
    glow: 'group-hover:text-yellow-400',
  },
  {
    to: '/play',
    icon: '▶️',
    label: 'Play Now',
    desc: 'Jump into a game instantly',
    color: 'from-purple-500/20 to-purple-600/5 border-purple-500/20 hover:border-purple-500/50',
    glow: 'group-hover:text-purple-400',
  },
];

const MARQUEE_TEXT = '404 • QUEST NOT FOUND • SIGNAL LOST • PAGE MISSING • ROUTE UNDEFINED • ';

const NotFoundPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center relative overflow-hidden pt-20">

      {/* Background: african pattern + radial glows */}
      <div className="absolute inset-0 african-pattern opacity-40 pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-primary-500/[0.04] rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute bottom-1/3 right-1/4 w-[500px] h-[500px] bg-secondary-400/[0.04] rounded-full blur-3xl animate-pulse-slow animation-delay-300" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-primary-500/[0.03] to-transparent rounded-full" />
      </div>

      {/* Scan line animation */}
      <style>{`
        @keyframes scan {
          0%   { top: 0%; opacity: 0; }
          5%   { opacity: 1; }
          95%  { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        .scan-line {
          animation: scan 3s linear infinite;
        }
        @keyframes glitch-clip {
          0%, 88%, 100% { clip-path: none; transform: translate(0); }
          90% { clip-path: polygon(0 20%, 100% 20%, 100% 40%, 0 40%); transform: translate(-3px, 2px); }
          92% { clip-path: polygon(0 60%, 100% 60%, 100% 80%, 0 80%); transform: translate(3px, -2px); }
          94% { clip-path: polygon(0 10%, 100% 10%, 100% 30%, 0 30%); transform: translate(-2px, -2px); }
          96% { clip-path: none; transform: translate(2px, 1px); }
        }
        .glitch-404 { animation: glitch-clip 4s infinite; }
        .glitch-404-before {
          content: attr(data-text);
          animation: glitch-clip 4s 0.1s infinite;
          color: #ff6b35;
          opacity: 0.6;
        }
      `}</style>

      {/* Main content */}
      <div
        className={`relative z-10 text-center px-6 max-w-4xl mx-auto w-full transition-all duration-700 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >

        {/* Error label */}
        <p className="section-subtitle mb-6 tracking-[0.4em]">SIGNAL LOST</p>

        {/* 404 */}
        <div className="relative inline-block mb-2 select-none">
          <span
            data-text="404"
            className="glitch-404 font-gaming font-black text-gradient leading-none block"
            style={{ fontSize: 'clamp(7rem, 20vw, 14rem)' }}
          >
            404
          </span>
          {/* Scan line over the number */}
          <div className="scan-line absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-secondary-400/60 to-transparent pointer-events-none" />
        </div>

        {/* Title */}
        <h1 className="font-gaming text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 glow-orange">
          QUEST NOT FOUND
        </h1>

        {/* Attempted path */}
        {location.pathname !== '/' && (
          <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 mb-6">
            <span className="text-gray-600 text-xs font-mono">requested path:</span>
            <span className="text-primary-400 text-xs font-mono truncate max-w-[260px]">
              {location.pathname}
            </span>
          </div>
        )}

        {/* Description */}
        <p className="text-gray-400 text-base md:text-lg max-w-lg mx-auto mb-10 leading-relaxed">
          This page has been lost in the digital savanna. The route you're chasing
          doesn't exist — or has moved deeper into the wild.
        </p>

        {/* Quick navigation cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          {QUICK_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`group relative bg-gradient-to-br ${link.color} border rounded-2xl p-5 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-lg`}
            >
              <span className="text-3xl mb-3 block animate-float" style={{ animationDelay: `${QUICK_LINKS.indexOf(link) * 0.3}s` }}>
                {link.icon}
              </span>
              <p className={`text-white font-semibold text-sm transition-colors duration-200 ${link.glow}`}>
                {link.label}
              </p>
              <p className="text-gray-500 text-xs mt-1 leading-snug">{link.desc}</p>
            </Link>
          ))}
        </div>

        {/* CTA buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/" className="btn-primary inline-block text-center">
            ← Return to Home
          </Link>
          <button
            onClick={() => navigate(-1)}
            className="btn-secondary"
          >
            Go Back
          </button>
        </div>

      </div>

      {/* Bottom marquee */}
      <div className="absolute bottom-0 left-0 right-0 border-t border-white/[0.06] py-2.5 overflow-hidden pointer-events-none">
        <div className="flex animate-marquee whitespace-nowrap">
          {Array(6).fill(MARQUEE_TEXT).map((text, i) => (
            <span key={i} className="font-gaming text-[10px] tracking-widest text-white/[0.07] mx-2">
              {text}
            </span>
          ))}
        </div>
      </div>

    </div>
  );
};

export default NotFoundPage;
